import PlainDate from '@Domain/PlainDate';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatWholeEuros from '@UI/Pages/Administration/AiCosts/Helpers/formatWholeEuros';
import isCurrentMonthPeriod from '@UI/Pages/Administration/AiCosts/Helpers/isCurrentMonthPeriod';
import useAiCostLimits from '@UI/Pages/Administration/Shared/Hooks/useAiCostLimits';
import describeLimitSubject from '@UI/Pages/Administration/AiCosts/CostSummary/MonthlyLimit/Helpers/describeLimitSubject';
import LimitGauge from '@UI/Pages/Administration/AiCosts/CostSummary/MonthlyLimit/LimitGauge';
import describeFilteredPerimeter from '@UI/Pages/Administration/AiCosts/CostSummary/MonthlyLimit/Helpers/describeFilteredPerimeter';
import projectMonthEnd from '@UI/Pages/Administration/AiCosts/CostSummary/MonthlyLimit/Helpers/projectMonthEnd';
import resolveApplicableLimit from '@UI/Pages/Administration/AiCosts/CostSummary/MonthlyLimit/Helpers/resolveApplicableLimit';
import type { AdministrationPath } from '@UI/Routing/administrationSections';
import type AiCostsFilterView from '@UI/Pages/Administration/AiCosts/Filters/AiCostsFilterView';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import type AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from '@verclam/synapse';

const LIMITS_PATH: AdministrationPath = '/administration/ai-cost-limits';

const LEVEL_RANKS: Readonly<Record<AiCostLimitView['level'], number>> = {
	account: 0,
	organization: 1,
	facility: 2,
	user: 3,
};

const monthFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'long' });

const blockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: 1rem 1.25rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const headerStyle = css`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 0.5rem 1rem;
`;

const titleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const linkStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.accentDark};
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly accountId: number;
	readonly summary: AiCostSummaryView;
	readonly filters: AiCostsFilterView;
};

const resolveFilterRank = (filters: AiCostsFilterView): number => {
	if (!filters.hasScope) {
		return 0;
	}

	if (filters.userId !== null) {
		return LEVEL_RANKS.user;
	}

	return filters.facilityId !== null ? LEVEL_RANKS.facility : LEVEL_RANKS.organization;
};

/** Loads the limits and shows the one that bounds the filtered scope; renders nothing while they are unavailable. */
const LimitBlock: React.FC<Props> = ({ accountId, summary, filters }) => {
	const filterOptions = useSelector((state) => state.aiCostsStore.filterOptions);

	const { data: limits, error } = useAiCostLimits(accountId);

	const today = PlainDate.today();
	const isCurrentMonth = isCurrentMonthPeriod(filters, today);
	const isMonthToDate =
		isCurrentMonth && filters.to.year === today.year && filters.to.month === today.month;
	const monthName = monthFormatter.format(today.toCalendar());

	const limit = React.useMemo(
		() => (limits === null ? null : resolveApplicableLimit(limits, filters, filterOptions)),
		[limits, filters, filterOptions],
	);

	const part = React.useMemo(() => {
		const startsOnFirst = filters.from.day === 1 && isCurrentMonth;
		const isWiderThanFilter = limit !== null && LEVEL_RANKS[limit.level] < resolveFilterRank(filters);

		if (!startsOnFirst || !isWiderThanFilter) {
			return null;
		}

		return {
			perimeter: describeFilteredPerimeter(filters, filterOptions),
			costAmount: summary.costAmount,
		};
	}, [filters, filterOptions, limit, isCurrentMonth, summary.costAmount]);

	if (error !== null || limits === null) {
		return null;
	}

	return (
		<div css={blockStyle}>
			<ConditionalLoader
				data={isCurrentMonth}
				fallback={
					<p css={noteStyle}>
						Le plafond est mensuel : choisissez une période du mois en cours pour voir la part
						consommée.
					</p>
				}
			>
				<ConditionalLoader
					data={limit}
					fallback={
						<div css={headerStyle}>
							<p css={titleStyle}>Aucun plafond mensuel</p>
							<Link css={linkStyle} to={LIMITS_PATH}>
								Voir les plafonds →
							</Link>
						</div>
					}
				>
					{(applicable) => (
						<React.Fragment>
							<div css={headerStyle}>
								<p css={titleStyle}>
									{`Plafond ${describeLimitSubject(applicable, filterOptions)} : ${formatWholeEuros(applicable.monthlyAmount)} · ${applicable.percent} % consommé`}
								</p>
								<Link css={linkStyle} to={LIMITS_PATH}>
									Voir les plafonds →
								</Link>
							</div>
							<LimitGauge
								limit={applicable}
								monthName={monthName}
								projectedPercent={
									isMonthToDate && filters.to.day === today.day
										? projectMonthEnd(applicable.percent, today)
										: null
								}
								part={part}
							/>
						</React.Fragment>
					)}
				</ConditionalLoader>
			</ConditionalLoader>
		</div>
	);
};

export default LimitBlock;
